import styled from '@grebban/style-system-react';
import Background from '@sportson/core-web/components/Background';
import { type BackgroundColorProps } from '@sportson/core-web/components/BackgroundColor';
import { type BackgroundImageProps } from '@sportson/core-web/components/BackgroundImage';
import { type BackgroundVideoProps } from '@sportson/core-web/components/BackgroundVideo';
import { type ButtonProps } from 'components/Button';
import { type CssColorKey } from 'config/branding/colors';
import { type CssVariable } from 'config/branding/types';
import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import { type WordpressACFWysiwyg } from 'libs/wordpress/types/acf/fields';
import Button from 'components/Button';
import Text from 'components/Text';

const Div = styled('div')``;

const ContentWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
    height: 100%;
`;

const StyledBackground = styled(Background)`
    flex: 1;
    border-radius: 4px;
    overflow: hidden;
`;

const Wrapper = styled('div')`
    display: flex;

    > * {
        width: 100%;
    }
`;

const TextAndButton = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ButtonWrapper = styled('div')`
    display: flex;
    flex-direction: row;
    gap: 12px;
`;

export interface DiscoverComponentPropsHeading {
    text: WordpressACFWysiwyg;
    type?: 'default' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    color?: CssVariable<CssColorKey>;
}

export interface DiscoverComponentPropsParagraph {
    text: WordpressACFWysiwyg;
    color?: CssVariable<CssColorKey>;
}

export type DiscoverComponentAlignmentProps = 'flex-start' | 'center' | 'flex-end';

export interface DiscoverComponentProps extends Record<string, unknown> {
    backgroundColor?: BackgroundColorProps;
    backgroundImage?: BackgroundImageProps;
    backgroundVideo?: BackgroundVideoProps;
    heading?: DiscoverComponentPropsHeading;
    buttons?: ButtonProps[];
    paragraph?: DiscoverComponentPropsParagraph;
    contentLink?: string;
    aspectRatio?: (string | null)[] | string;
    headingTypography?: (string | null)[] | string;
}

const DiscoverComponent = ({
    backgroundColor,
    backgroundImage,
    backgroundVideo,
    heading,
    buttons,
    paragraph,
    contentLink,
    aspectRatio,
    headingTypography,
    ...rest
}: DiscoverComponentProps) => (
    <ContentWrapper>
        <Div aspectRatio={aspectRatio}>
            <StyledBackground
                backgroundVideo={backgroundVideo}
                backgroundImage={backgroundImage}
                backgroundColor={backgroundColor}
            />
        </Div>

        <Wrapper {...rest}>
            {/* TODO: Add logic for heading size */}
            {heading && (
                <Wysiwyg
                    data={heading.text}
                    TextComponent={(props) => (
                        <Text
                            textAlign="left"
                            as={heading.type || 'h3'}
                            color={heading.color || 'var(--discover-module-text-color-default)'}
                            typography={headingTypography}
                            {...props}
                        />
                    )}
                />
            )}

            <TextAndButton>
                {paragraph && (
                    <Wysiwyg
                        data={paragraph.text}
                        TextComponent={(props) => (
                            <Text
                                textAlign="left"
                                $as="p"
                                color={paragraph.color || 'var(--discover-module-text-color-default)'}
                                typography="Content/16_150_0_350"
                                {...props}
                            />
                        )}
                    />
                )}
                {buttons && (
                    <ButtonWrapper>
                        {buttons.map((button) => (
                            <Button
                                key={`${button.url}`}
                                typography="UI/16_100_0_450"
                                theme={button.theme || 'secondary'}
                                to={button.url}
                            >
                                {button.children}
                            </Button>
                        ))}
                    </ButtonWrapper>
                )}
            </TextAndButton>
        </Wrapper>
    </ContentWrapper>
);

export default DiscoverComponent;
